import { ADMIN_HOME_PATH } from '../navigation/adminNavigation';

type DestinationOptions = {
  nextParam?: string | null;
  locationStateFrom?: string | null;
  storedRedirect?: string | null;
  storedState?: string | null;
  fallbackPath?: string;
};

type MfaTargetOptions = {
  nextParam?: string | null;
  challengeIdParam?: string | null;
  storedRedirect?: string | null;
  storedState?: string | null;
  fallbackPath?: string;
};

type LoginDecisionOptions = {
  isLoading: boolean;
  isAuthenticated: boolean;
  allowPendingMfaAccess: boolean;
};

type LandingDecisionOptions = {
  isLoading: boolean;
  isAuthenticated: boolean;
  hasLoginError: boolean;
  isLogout: boolean;
};

type PathCandidate = string | null | undefined;

const normalizeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = normalized.length % 4;
  return remainder === 0 ? normalized : `${normalized}${'='.repeat(4 - remainder)}`;
};

const decodeStatePayload = (rawState: string | null): { originalUrl?: string } | null => {
  if (!rawState) return null;

  try {
    return JSON.parse(atob(normalizeBase64Url(rawState)));
  } catch {
    return null;
  }
};

const isSafeAppPath = (candidate: PathCandidate): candidate is string => {
  if (!candidate) return false;
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return false;
  if (candidate === '/mfa' || candidate.startsWith('/mfa?')) return false;
  if (candidate === '/landing' || candidate.startsWith('/landing?')) return false;
  return true;
};

export const resolveStoredRedirectTarget = (storedRedirect?: string | null, storedState?: string | null): string | null => {
  if (isSafeAppPath(storedRedirect)) {
    return storedRedirect;
  }

  const originalUrl = decodeStatePayload(storedState)?.originalUrl;
  return isSafeAppPath(originalUrl) ? originalUrl : null;
};

export const resolvePostAuthDestination = ({
  nextParam,
  locationStateFrom,
  storedRedirect,
  storedState,
  fallbackPath = ADMIN_HOME_PATH,
}: DestinationOptions): string => {
  for (const candidate of [
    nextParam,
    locationStateFrom,
    resolveStoredRedirectTarget(storedRedirect, storedState),
  ]) {
    if (isSafeAppPath(candidate)) {
      return candidate;
    }
  }

  return fallbackPath;
};

export const buildMfaRedirectTarget = ({
  nextParam,
  challengeIdParam,
  storedRedirect,
  storedState,
  fallbackPath = ADMIN_HOME_PATH,
}: MfaTargetOptions): string => {
  const params = new URLSearchParams();
  const destination = resolvePostAuthDestination({
    nextParam,
    locationStateFrom: null,
    storedRedirect,
    storedState,
    fallbackPath,
  });

  if (isSafeAppPath(destination)) {
    params.set('next', destination);
  }

  if (challengeIdParam && challengeIdParam.trim()) {
    params.set('challengeId', challengeIdParam);
  }

  return `/mfa${params.size > 0 ? `?${params.toString()}` : ''}`;
};

export const buildPendingMfaRedirect = (pathname: string, search: string, hash: string): string =>
  buildMfaRedirectTarget({
    nextParam: `${pathname}${search}${hash}`,
    fallbackPath: ADMIN_HOME_PATH,
  });

export const buildPendingMfaRedirectWithChallenge = (
  pathname: string,
  search: string,
  hash: string,
  challengeId?: string | null,
): string =>
  buildMfaRedirectTarget({
    nextParam: `${pathname}${search}${hash}`,
    challengeIdParam: challengeId,
    fallbackPath: ADMIN_HOME_PATH,
  });

export const shouldStartInteractiveAdminLogin = ({
  isLoading,
  isAuthenticated,
  allowPendingMfaAccess,
}: LoginDecisionOptions): boolean => {
  if (isLoading || isAuthenticated) {
    return false;
  }

  return !allowPendingMfaAccess;
};

export const shouldCheckAdminMfaGuard = ({
  isLoading,
  isAuthenticated,
  allowPendingMfaAccess,
}: LoginDecisionOptions): boolean => {
  if (isLoading || allowPendingMfaAccess) {
    return false;
  }

  return isAuthenticated;
};

export const shouldAutoEnterAdminDashboard = ({
  isLoading,
  isAuthenticated,
  hasLoginError,
  isLogout,
}: LandingDecisionOptions): boolean => {
  if (isLoading || !isAuthenticated) {
    return false;
  }

  return !hasLoginError && !isLogout;
};
